import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, styled, Typography, Skeleton } from '@mui/material';

import {
  SyncedTripLegWithCareLevel,
  TripLeg,
} from '../../../../data/models/reservation.model';

import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';

import {
  selectEstimatedTripLegPricing,
  selectLoadedEstimatedTripLegPricing,
  selectLoadingEstimatedTripLegPricing,
} from '../../../../data/accounting.slice';
import {
  fetchDriverByIdAsync,
  selectDriversById,
} from '../../../../data/driver.slice';

import dateUtils from '../../../../services/DateUtils';
import { financialUtils } from '../../../../services/financialUtils';
import reservationUtils from '../../../../services/reservationUtils';
import tripLegUtils from '../../../../services/triplegUtils';

import { phoneUtils } from '../../../../services/phoneUtils';

const PREFIX = 'ProviderCallSummary';
const classes = {
  container: `${PREFIX}-container`,
  currentTripLeg: `${PREFIX}-currentTripLeg`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: 528,
  padding: theme.spacing(4),

  [`& .${classes.currentTripLeg}`]: {
    color: theme.palette.error.main,
  },
}));

const StyledSummaryBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  marginTop: theme.spacing(2),

  '& .MuiTypography-root': {
    paddingBottom: 0,
  },
}));

interface TripSummaryProps {
  tripLeg: SyncedTripLegWithCareLevel;
  tripLegsInReservation: TripLeg[];
  providerContactName: string;
  isLoading: boolean;
  showMemberName?: boolean;
  showDistance?: boolean;
  showPrice?: boolean;
  showServiceLevel?: boolean;
  showPickupTime?: boolean;
  showPickupLocation?: boolean;
  showDropoffLocation?: boolean;
  showAssignedDriver?: boolean;
  showPickupPhone?: boolean;
  showDropoffPhone?: boolean;
}

const TripSummary = ({
  tripLeg,
  tripLegsInReservation,
  providerContactName,
  isLoading,
  showServiceLevel = true,
  showMemberName = true,
  showDistance = true,
  showPrice = true,
  showAssignedDriver = true,
  showPickupPhone = true,
  showDropoffPhone = true,
  showPickupTime = true,
  showPickupLocation = true,
  showDropoffLocation = true,
}: TripSummaryProps) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const estimatedTripLegPricing = useAppSelector(selectEstimatedTripLegPricing);
  const loadingEstimatedTripLegPricing = useAppSelector(
    selectLoadingEstimatedTripLegPricing,
  );
  const loadedEstimatedTripLegPricing = useAppSelector(
    selectLoadedEstimatedTripLegPricing,
  );
  const driversById = useAppSelector(selectDriversById);

  useEffect(() => {
    const seen = new Set<number>();
    tripLegsInReservation.forEach((leg) => {
      const id = leg.driverID;
      if (id && !leg.driver && !seen.has(id)) {
        seen.add(id);
        dispatch(fetchDriverByIdAsync(id));
      }
    });
  }, [dispatch, tripLegsInReservation]);

  const totalDistance = tripLegUtils.formatDistance({
    distance: tripLegsInReservation.reduce(
      (acc, leg) => acc + Number(leg?.estimatedDistance ?? 0),
      0,
    ),
    unitPlural: t('tripSummary.miles'),
    unitSingular: t('tripSummary.mile'),
  });

  const totalPrice = tripLegsInReservation.reduce(
    (acc, leg) =>
      acc + Number(estimatedTripLegPricing[leg.tripLegReferenceID ?? ''] ?? 0),
    0,
  );

  const passengerCareLevel = useMemo(() => {
    if (tripLeg.tripModeBC === 'AMBI') {
      return `AMB${tripLeg.passengerCareLevelBC}`;
    }

    return `${tripLeg.tripModeBC}${tripLeg.passengerCareLevelBC}`;
  }, [tripLeg]);

  return (
    <StyledPaper elevation={2} className={classes.container}>
      <Typography variant="h3">{t('tripSummary.summary')}</Typography>

      <StyledSummaryBox>
        <Typography>
          {t('tripSummary.memberName')}:{' '}
          <Typography component="span" variant="body1" className="bold">
            {tripLeg.passengerFirstName} {tripLeg.passengerLastName}
          </Typography>
        </Typography>

        <Typography>
          {t('tripSummary.serviceLevel')}:{' '}
          <Typography component="span" variant="body1" className="bold">
            {passengerCareLevel}
          </Typography>
        </Typography>

        <Typography>
          {t('tripSummary.totalDistance')}:{' '}
          <Typography component="span" variant="body1" className="bold">
            {`${totalDistance}`}
          </Typography>
        </Typography>

        <Typography>
          {`${t('tripSummary.totalPrice')}: `}
          <Typography component="span" variant="body1" className="bold">
            {financialUtils.formatPrice(totalPrice)}
          </Typography>
        </Typography>
      </StyledSummaryBox>

      {tripLegsInReservation.map((tripLegInReservation, idx) => {
        const classNames = [];
        const currentTripLeg =
          tripLegInReservation.tripLegReferenceID ===
          tripLeg.tripLegReferenceID;
        if (currentTripLeg) {
          classNames.push(classes.currentTripLeg);
        }
        const price = financialUtils.formatPrice(
          Number(tripLegInReservation.payableAmount ?? 0),
        );
        const distance = tripLegUtils.formatDistance({
          distance: tripLegInReservation.estimatedDistance ?? 0,
          unitPlural: t('tripSummary.miles'),
          unitSingular: t('tripSummary.mile'),
        });
        const label = `${t('tripSummary.tripLeg')} ${idx + 1} (${currentTripLeg ? 'Current - ' : ''}${tripLegInReservation.tripLegReferenceID})`;
        return (
          <StyledSummaryBox
            key={`trip-leg-${tripLegInReservation.tripLegReferenceID}`}>
            <Typography variant="subtitle1" className={classNames.join(' ')}>
              {label}
            </Typography>

            {showPickupTime && (
              <Typography>
                {t('tripSummary.pickupTime')}:{' '}
                <Typography
                  component="span"
                  variant="body1"
                  className={`bold ${classNames.join(' ')}`}>
                  {tripLegInReservation.isWillCall
                    ? t('tripSummary.willCall')
                    : dateUtils.formatPickupTime(
                        tripLegInReservation.pickUpDueDateTime,
                        tripLegInReservation.pickUpAddress.timeZone!,
                      )}
                </Typography>
              </Typography>
            )}

            {showPickupLocation && (
              <Typography>
                {t('tripSummary.pickupLocation')}:{' '}
                <Typography component="span" variant="body1" className="bold">
                  {`${tripLegInReservation.pickUpAddress.addressLine1}, ${tripLegInReservation.pickUpAddress.city}, ${tripLegInReservation.pickUpAddress.stateCode}, ${tripLegInReservation.pickUpAddress.zipCode}`}
                </Typography>
              </Typography>
            )}

            {showPickupPhone && (
              <Typography>
                {t('tripSummary.pickupPhone')}:{' '}
                <Typography component="span" variant="body1" className="bold">
                  {phoneUtils.formatPhoneString(
                    tripLegInReservation.pickUpPhone?.phoneNumber ?? '',
                  )}
                </Typography>
              </Typography>
            )}

            {showDropoffLocation && (
              <Typography>
                {t('tripSummary.dropoffLocation')}:{' '}
                <Typography component="span" variant="body1" className="bold">
                  {`${tripLegInReservation.dropOffAddress.addressLine1}, ${tripLegInReservation.dropOffAddress.city}, ${tripLegInReservation.dropOffAddress.stateCode}, ${tripLegInReservation.dropOffAddress.zipCode}`}
                </Typography>
              </Typography>
            )}

            {showDropoffPhone && (
              <Typography>
                {t('tripSummary.dropoffPhone')}:{' '}
                <Typography component="span" variant="body1" className="bold">
                  {phoneUtils.formatPhoneString(
                    tripLegInReservation.dropOffPhone?.phoneNumber ?? '',
                  )}
                </Typography>
              </Typography>
            )}

            {showDistance && (
              <Typography>
                {t('tripSummary.distance')}:{' '}
                <Typography component="span" variant="body1" className="bold">
                  {distance}
                </Typography>
              </Typography>
            )}

            {showPrice && loadingEstimatedTripLegPricing && (
              <Skeleton variant="text" width={100} height={24} />
            )}

            {showPrice && loadedEstimatedTripLegPricing && (
              <Typography>
                {t('tripSummary.price')}:{' '}
                <Typography component="span" variant="body1" className="bold">
                  {financialUtils.formatPrice(
                    Number(
                      estimatedTripLegPricing[
                        tripLegInReservation.tripLegReferenceID ?? ''
                      ] ?? 0,
                    ),
                  )}
                </Typography>
              </Typography>
            )}

            {showAssignedDriver &&
              (() => {
                const resolvedDriver =
                  tripLegInReservation.driver ??
                  (tripLegInReservation.driverID
                    ? driversById[tripLegInReservation.driverID]
                    : undefined);
                const driverName =
                  reservationUtils.getDriverName(resolvedDriver);
                const displayName =
                  driverName ??
                  (tripLegInReservation.driverID
                    ? ''
                    : t('tripSummary.unassignedDriver'));
                return (
                  <Typography>
                    {t('tripSummary.driver')}:{' '}
                    <Typography
                      component="span"
                      variant="body1"
                      className="bold">
                      {displayName}
                    </Typography>
                  </Typography>
                );
              })()}
          </StyledSummaryBox>
        );
      })}
    </StyledPaper>
  );
};

export { TripSummary };
